/**
 * abilityCurve — the single source of truth for which abilities a class may
 * train at a given level, derived from the authored unlock curve
 * (content/classes/ability_curve.toml → ABILITY_UNLOCK_LEVELS).
 *
 * Used by:
 *   - server: starting kit on character creation and legacy-state migration
 *   - client: trainer eligibility and progression previews
 *
 * (2026-06-28 — real ability-learning curve. Before this, classes started with
 * the full 10-ability demo hotbar and nothing was gated by level.)
 */

import { ABILITY_UNLOCK_LEVELS, type AbilityUnlock } from '../content/generated/hotbars.gen';

function curveFor(heroClass: string | undefined | null): AbilityUnlock[] {
  if (!heroClass) return [];
  return ABILITY_UNLOCK_LEVELS[heroClass.toLowerCase()] ?? [];
}

/** The level at which `abilityId` is learned for this class, or null if the
 *  ability isn't on the class curve (e.g. browser-only unlockables). */
export function unlockLevelForAbility(heroClass: string, abilityId: string): number | null {
  const entry = curveFor(heroClass).find(u => u.abilityId === abilityId);
  return entry ? entry.level : null;
}

/**
 * Legacy class+level eligibility check. Abilities NOT on the curve are treated
 * as available for compatibility. Runtime casts and hotbar assignments must
 * use `isAbilityTrained` instead; reaching an unlock level does not purchase
 * trainer-owned abilities.
 */
export function isAbilityLearned(heroClass: string, level: number, abilityId: string): boolean {
  const lvl = unlockLevelForAbility(heroClass, abilityId);
  if (lvl === null) return true; // off-curve → not gated
  return level >= lvl;
}

/** True only when the server-persisted trained list contains `abilityId`. */
export function isAbilityTrained(
  learnedAbilities: readonly string[] | undefined | null,
  abilityId: string,
): boolean {
  return learnedAbilities?.includes(abilityId) ?? false;
}

/** True when a trainer may teach this class ability at the player's current
 * level. Unlike the legacy cast gate, off-curve abilities fail closed here. */
export function isAbilityEligible(heroClass: string, level: number, abilityId: string): boolean {
  const unlockLevel = unlockLevelForAbility(heroClass, abilityId);
  return unlockLevel !== null && level >= unlockLevel;
}

/** Abilities that become trainer-eligible EXACTLY at `level`. */
export function abilitiesEligibleAtLevel(heroClass: string, level: number): string[] {
  return curveFor(heroClass).filter(u => u.level === level).map(u => u.abilityId);
}

/** All curve abilities learned by `level` (unlock level ≤ level). */
export function learnedAbilitiesUpTo(heroClass: string, level: number): string[] {
  return curveFor(heroClass).filter(u => u.level <= level).map(u => u.abilityId);
}

/** The level-1 starting kit (auto + core) for a class. */
export function startingKitAbilities(heroClass: string): string[] {
  return abilitiesEligibleAtLevel(heroClass, 1);
}

/** Full ordered curve for a class (for progression UI and training previews). */
export function abilityCurveFor(heroClass: string): AbilityUnlock[] {
  return curveFor(heroClass);
}
